import { useEffect, useRef, useState } from 'react'

import { useQuery } from '@apollo/client'
import { F0AvatarIcon, F0AvatarPerson } from '@factorialco/f0-react'
import { Spinner } from '@factorialco/f0-react/dist/experimental'
import { LogoAvatar } from '@factorialco/f0-react/icons/app'
import { AssetActivityLogDocument } from 'generated/resources/graphql'
import moment from 'moment'

import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

interface ActivityLogProps {
  assetId: string
}

interface ActivityLogItem {
  id: string
  updatedAt: string
  action?: string | null
  status?: string | null
  ownerId?: number | null
  locationId?: number | null
  serialNumber?: string | null
  purchasePriceCents?: number | null
  currency?: string | null
  label?: string | null
  deletedAt?: string | null
  description?: string | null
  authorName?: string | null
  author?: {
    id: number
    firstName?: string | null
    lastName?: string | null
  } | null
}

const ActivityLog = ({ assetId }: ActivityLogProps) => {
  const { data, loading, error } = useQuery(AssetActivityLogDocument, {
    variables: { assetId },
    skip: !assetId,
    fetchPolicy: 'no-cache', // Disable caching to prevent object reference issues
  })
  const lastAvatarRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [lineBottom, setLineBottom] = useState('1rem')

  const activityLogsConnection = data?.itManagement?.assetActivityLogsConnection
  const activities: ActivityLogItem[] = activityLogsConnection?.nodes || []

  useEffect(() => {
    if (lastAvatarRef.current && containerRef.current && activities.length > 0) {
      // Use requestAnimationFrame to ensure layout is complete
      requestAnimationFrame(() => {
        if (lastAvatarRef.current && containerRef.current) {
          const avatarBottom = lastAvatarRef.current.getBoundingClientRect().bottom
          const containerBottom = containerRef.current.getBoundingClientRect().bottom
          const distanceFromBottom = containerBottom - avatarBottom
          setLineBottom(`${Math.max(0, distanceFromBottom)}px`)
        }
      })
    }
  }, [data, activities.length])

  if (loading) {
    return (
      <div className="flex items-center justify-center p-4">
        <Spinner size="small" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-4 text-sm text-red-600">
        {i18n.t('it_management.asset_details.activity_log_error')}
      </div>
    )
  }

  if (activities.length === 0) {
    return (
      <div className="p-4 text-sm text-f1-foreground-secondary">
        {i18n.t('it_management.asset_details.no_activity')}
      </div>
    )
  }

  const getActivityDescription = (activity: ActivityLogItem) => {
    if (activity.deletedAt) {
      return i18n.t('it_management.asset_details.activity_deleted')
    }

    if (activity.action) {
      switch (activity.action) {
        case 'deleted':
          return i18n.t('it_management.asset_details.activity_deleted')
        case 'created':
          return i18n.t('it_management.asset_details.activity_created')
        case 'updated':
          return i18n.t('it_management.asset_details.activity_updated')
        case 'ownership_changed':
          return i18n.t('it_management.asset_details.activity_ownership_changed')
        case 'status_changed':
          return i18n.t('it_management.asset_details.activity_status_changed')
        default:
          return i18n.t('it_management.asset_details.activity_updated')
      }
    }

    return i18n.t('it_management.asset_details.activity_updated')
  }

  const formatRelativeTime = (dateString: string): string => {
    const date = stringToDate(dateString, 'backend_datetime')
    const now = moment()
    const diffInMinutes = now.diff(date, 'minutes')
    const diffInHours = now.diff(date, 'hours')
    const diffInDays = now.diff(date, 'days')
    const diffInMonths = now.diff(date, 'months')
    const diffInYears = now.diff(date, 'years')

    if (diffInYears >= 1) {
      return i18n.t('it_management.activity_logs.relative_time.more_than_year')
    } else if (diffInMonths >= 1) {
      const months = diffInMonths
      if (months === 1) {
        return i18n.t('it_management.activity_logs.relative_time.months', { count: months })
      }
      return i18n.t('it_management.activity_logs.relative_time.months_other', { count: months })
    } else if (diffInDays >= 1) {
      const days = diffInDays
      if (days === 1) {
        return i18n.t('it_management.activity_logs.relative_time.days', { count: days })
      }
      return i18n.t('it_management.activity_logs.relative_time.days_other', { count: days })
    } else if (diffInHours >= 1) {
      if (diffInHours === 1) {
        return i18n.t('it_management.activity_logs.relative_time.hours', { count: diffInHours })
      }
      return i18n.t('it_management.activity_logs.relative_time.hours_other', { count: diffInHours })
    } else {
      const minutes = Math.max(0, diffInMinutes)
      if (minutes === 1) {
        return i18n.t('it_management.activity_logs.relative_time.minutes', { count: minutes })
      }
      return i18n.t('it_management.activity_logs.relative_time.minutes_other', {
        count: minutes,
      })
    }
  }

  const formatFullDateTime = (dateString: string): string => {
    const date = stringToDate(dateString, 'backend_datetime')
    const datePart = i18nDateToString(date, 'ui_full_month_year')
    const localeData = moment.localeData()
    const uses12Hour =
      localeData.longDateFormat('LT').includes('A') || localeData.longDateFormat('LT').includes('a')
    const timeFormat = uses12Hour ? 'h:mm A' : 'HH:mm'
    const timePart = date.format(timeFormat)
    return `${datePart} ${timePart}`
  }

  return (
    <div ref={containerRef} className="flex flex-col pl-4 pt-4 pb-4 relative">
      {/* Continuous vertical line in the background */}
      <div
        className="w-px absolute"
        style={{
          backgroundColor: 'rgba(5, 35, 72, 0.14)',
          top: '1rem',
          bottom: lineBottom,
          left: 'calc(1rem + 16px)',
          transform: 'translateX(-50%)',
        }}
      />
      {activities.map((activity, index) => {
        const isLast = index === activities.length - 1
        const isSystemMessage = !activity.author
        return (
          <div
            key={`activity-${activity.updatedAt}-${activity.action}-${activity.status}-${activity.ownerId}-${activity.locationId}-${index}`}
            className={`flex min-w-0 ${!isLast ? 'mb-4' : ''}`}
          >
            <div
              className="flex flex-col items-center mr-3 flex-shrink-0 relative z-10"
              style={{ width: '32px' }}
            >
              <div
                ref={isLast ? lastAvatarRef : null}
                className="rounded-full"
                style={{
                  backgroundColor: 'white',
                  padding: '5px',
                }}
              >
                {isSystemMessage ? (
                  <F0AvatarIcon icon={LogoAvatar} size="sm" />
                ) : (
                  <F0AvatarPerson
                    firstName={activity.author?.firstName || ''}
                    lastName={activity.author?.lastName || ''}
                    size="sm"
                  />
                )}
              </div>
              {isLast && (
                <div
                  className="absolute w-px bg-white"
                  style={{
                    top: '100%',
                    bottom: '-1000px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 5,
                  }}
                />
              )}
            </div>
            <div className="flex flex-col flex-1 min-w-0">
              <p className="text-sm mb-1 leading-5">
                {(() => {
                  const description = activity.description || getActivityDescription(activity)
                  const authorName =
                    activity.authorName || i18n.t('it_management.activity_logs.system')

                  if (description.startsWith(authorName)) {
                    const actionPart = description.slice(authorName.length).trim()
                    return (
                      <>
                        <span className="font-semibold text-f1-foreground">{authorName}</span>
                        {actionPart && (
                          <span className="text-f1-foreground-secondary"> {actionPart}</span>
                        )}
                      </>
                    )
                  }

                  return <span className="text-f1-foreground-secondary">{description}</span>
                })()}
              </p>
              {activity.updatedAt && (
                <p
                  className="text-xs text-f1-foreground-secondary leading-4"
                  title={formatFullDateTime(activity.updatedAt)}
                >
                  {formatRelativeTime(activity.updatedAt)}
                </p>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default ActivityLog
